import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FieldTimeOutlined,
  HourglassOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import type { PermissionRequest } from "@p0security/shared/types/permission";
import {
  RequestStatus,
  RequestStatuses,
} from "@p0security/shared/types/request-status";
import { Tooltip } from "@p0security/ui";
import { isa } from "@p0security/util/types/is";
import { StyledTag } from "styles/StyledTags";

import { COLOR_MAP } from "../requestUtils";

const ICON_MAP: Partial<Record<keyof typeof COLOR_MAP, React.ReactNode>> = {
  "Pending Approval": <ClockCircleOutlined />,
  Approved: <CheckCircleOutlined />,
  Granted: <CheckCircleOutlined />,
  Denied: <CloseCircleOutlined />,
  Relinquished: <RollbackOutlined />,
  Revoked: <RollbackOutlined />,
  Expired: <FieldTimeOutlined />,
  "Timed Out": <HourglassOutlined />,
};

const STATUS_MAPPING: Record<RequestStatus, keyof typeof COLOR_MAP> = {
  EXPIRED_NOTIFIED: "Expired",
  EXPIRED: "Expired",
  NEW: "Request Created",
  PENDING_APPROVAL: "Pending Approval",
  PENDING_APPROVAL_ESCALATED: "Pending Approval",
  APPROVED: "Approved",
  DENIED: "Denied",
  REVOKED: "Relinquished",
  DONE: "Granted",
  DRAFT: "Not yet created",
  APPROVED_NOTIFIED: "Approved",
  DENIED_NOTIFIED: "Denied",
  ERRORED_NOTIFIED: "Errored",
  DONE_NOTIFIED: "Granted",
  ERRORED_ERRORED: "Errored",
  ERRORED: "Errored",
  EXPIRY_SUBMITTED: "Expiring",
  REVOKE_SUBMITTED: "Relinquishing",
  REVOKED_NOTIFIED: "Relinquished",
  STAGED: "Staged",
  TRANSLATED: "N/A",
  CLEANUP_SUBMITTED: "Expiring",
  CLEANED_UP: "Expired",
  CLEANUP_ERRORED: "Errored",
  TIMED_OUT: "Timed Out",
  TIMED_OUT_NOTIFIED: "Timed Out",
} as const;

const isAdminRevocation = (request: PermissionRequest) =>
  !!request.revocationDetails?.email &&
  request.revocationDetails.email !== request.principal;

const REVOCATION_OVERRIDES: Partial<
  Record<RequestStatus, keyof typeof COLOR_MAP>
> = {
  REVOKE_SUBMITTED: "Revoking",
  REVOKED: "Revoked",
  REVOKED_NOTIFIED: "Revoked",
};

const TIMED_OUT_TOOLTIP_MESSAGE =
  "The request wasn't approved in time, and access was not granted.";

const TOOLTIP_MAPPING: Record<RequestStatus, string | undefined> = {
  APPROVED_NOTIFIED: undefined,
  APPROVED: undefined,
  CLEANUP_ERRORED: undefined,
  CLEANUP_SUBMITTED: undefined,
  CLEANED_UP: undefined,
  DENIED_NOTIFIED: undefined,
  DENIED: undefined,
  DONE_NOTIFIED: undefined,
  DONE: undefined,
  DRAFT: undefined,
  ERRORED_ERRORED: undefined,
  ERRORED_NOTIFIED: undefined,
  ERRORED: undefined,
  EXPIRED_NOTIFIED: undefined,
  EXPIRED: undefined,
  EXPIRY_SUBMITTED: undefined,
  NEW: undefined,
  PENDING_APPROVAL: undefined,
  PENDING_APPROVAL_ESCALATED: undefined,
  REVOKE_SUBMITTED: undefined,
  REVOKED_NOTIFIED: undefined,
  REVOKED: undefined,
  STAGED: undefined,
  TRANSLATED: undefined,
  TIMED_OUT: TIMED_OUT_TOOLTIP_MESSAGE,
  TIMED_OUT_NOTIFIED: TIMED_OUT_TOOLTIP_MESSAGE,
};

export const RequestStatusTag: React.FC<{
  request: PermissionRequest;
  style?: React.CSSProperties;
}> = ({ request, style }) => {
  const { status } = request;
  if (!isa(RequestStatuses, status)) return "Unknown";

  const friendly =
    (isAdminRevocation(request) && REVOCATION_OVERRIDES[status]) ||
    STATUS_MAPPING[status];
  const tooltip = TOOLTIP_MAPPING[status];

  return (
    <Tooltip title={tooltip}>
      <span>
        <StyledTag
          color={COLOR_MAP[friendly]}
          icon={ICON_MAP[friendly]}
          /* eslint-disable-line react/forbid-component-props */ style={{
            ...style,
            textAlign: "center",
            width: "100%",
            maxWidth: "15em",
          }}
          key={status}
          ellipsis={true}
        >
          {friendly}
        </StyledTag>
      </span>
    </Tooltip>
  );
};
